import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  MessageCircle, 
  Mic, 
  MicOff, 
  Send, 
  Trash2, 
  CheckCircle,
  Loader2,
  Bot,
  User,
  Clock,
  MapPin,
  Wrench,
  Building,
  Fuel,
  Settings
} from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BotInterventionsPage() {
  const navigate = useNavigate();
  const { activeSociete } = useWorkspace();
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [interventionCreated, setInterventionCreated] = useState(null);
  const [consommationCreated, setConsommationCreated] = useState(null);
  
  const messagesEndRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  
  // Get token from localStorage
  const getToken = () => localStorage.getItem('token');

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Initialize with welcome message
  useEffect(() => {
    if (messages.length === 0) {
      setMessages([{
        role: 'assistant',
        content: `Bonjour ! Je suis le **Bot Interventions**. 

Je suis là pour vous aider à enregistrer vos interventions rapidement. 

Dites-moi simplement ce que vous avez fait aujourd'hui, par exemple :
- *"J'ai fait 4 heures de labour chez Domaine du Gourget sur la parcelle Les Vignes"*
- *"Vendange machine pendant 6h pour le client Braja, parcelles Nord et Sud"*

Vous pouvez aussi utiliser le micro pour me dicter votre intervention ! 🎤`,
        timestamp: new Date().toISOString()
      }]);
    }
  }, []);

  const sendMessage = async (text) => {
    if (!text.trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      content: text,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);
    setInterventionCreated(null);
    setConsommationCreated(null);

    try {
      const response = await fetch(`${API_URL}/api/bot-interventions/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`,
          'X-Societe-Id': activeSociete?.id
        },
        body: JSON.stringify({
          message: text,
          session_id: sessionId
        })
      });

      const data = await response.json();

      if (response.ok) {
        // Update session ID
        if (data.session_id) {
          setSessionId(data.session_id);
        }

        // Add bot response
        const botMessage = {
          role: 'assistant',
          content: data.response,
          timestamp: new Date().toISOString()
        };
        setMessages(prev => [...prev, botMessage]);

        // Check if intervention was created
        if (data.intervention_created) {
          setInterventionCreated(data.intervention_created);
        }
        
        // Check if fuel consumption was recorded
        if (data.consommation_created) {
          setConsommationCreated(data.consommation_created);
        }
      } else {
        throw new Error(data.detail || 'Erreur du serveur');
      }
    } catch (error) {
      console.error('Error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `Désolé, une erreur s'est produite: ${error.message}`,
        timestamp: new Date().toISOString(),
        isError: true
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        await transcribeAudio(audioBlob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
    } catch (error) {
      console.error('Error starting recording:', error);
      alert('Impossible d\'accéder au microphone. Vérifiez les permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const transcribeAudio = async (audioBlob) => {
    setIsLoading(true);
    
    // Add a temporary message
    setMessages(prev => [...prev, {
      role: 'user',
      content: '🎤 Transcription en cours...',
      timestamp: new Date().toISOString(),
      isTranscribing: true
    }]);

    try {
      const formData = new FormData();
      formData.append('file', audioBlob, 'recording.webm');

      const response = await fetch(`${API_URL}/api/bot-interventions/transcribe`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'X-Societe-Id': activeSociete?.id
        },
        body: formData
      });

      const data = await response.json();

      // Remove transcribing message
      setMessages(prev => prev.filter(m => !m.isTranscribing));

      if (response.ok && data.text) {
        // Send the transcribed text to the bot
        await sendMessage(data.text);
      } else {
        throw new Error(data.detail || 'Erreur de transcription');
      }
    } catch (error) {
      console.error('Transcription error:', error);
      setMessages(prev => prev.filter(m => !m.isTranscribing));
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `Erreur de transcription: ${error.message}`,
        timestamp: new Date().toISOString(),
        isError: true
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const clearConversation = async () => {
    if (sessionId) {
      try {
        await fetch(`${API_URL}/api/bot-interventions/history/${sessionId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${getToken()}`,
            'X-Societe-Id': activeSociete?.id
          }
        });
      } catch (error) {
        console.error('Error clearing conversation:', error);
      }
    }
    
    setMessages([{
      role: 'assistant',
      content: `Conversation effacée. Comment puis-je vous aider ?`,
      timestamp: new Date().toISOString()
    }]);
    setSessionId(null);
    setInterventionCreated(null);
  };

  const formatMessage = (content) => {
    // Simple markdown-like formatting
    return content
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/```json([\s\S]*?)```/g, '<pre class="bg-gray-100 p-2 rounded text-xs overflow-x-auto">$1</pre>')
      .replace(/\n/g, '<br />');
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6" data-testid="bot-interventions-page">
      {/* Header */}
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Bot className="h-7 w-7 text-green-600" />
            Bot Interventions
          </h1>
          <p className="text-gray-600">Enregistrez vos interventions en parlant naturellement</p>
        </div>
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => navigate('/agents/config', { state: { selectedAgent: 'technique' } })}
          data-testid="agent-settings-btn"
        >
          <Settings className="h-4 w-4 mr-2" /> Paramètres
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Chat Section */}
        <div className="lg:col-span-2">
          <Card className="h-[calc(100vh-200px)] flex flex-col">
            <CardHeader className="border-b py-3">
              <div className="flex justify-between items-center">
                <CardTitle className="text-lg flex items-center gap-2">
                  <MessageCircle className="h-5 w-5" />
                  Conversation
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={clearConversation}>
                  <Trash2 className="h-4 w-4 mr-1" />
                  Effacer
                </Button>
              </div>
            </CardHeader>
            
            <CardContent className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((msg, idx) => (
                <div 
                  key={idx} 
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div 
                    className={`max-w-[80%] rounded-lg p-3 ${
                      msg.role === 'user' 
                        ? 'bg-green-600 text-white' 
                        : msg.isError 
                          ? 'bg-red-100 text-red-800'
                          : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      {msg.role === 'user' ? (
                        <User className="h-4 w-4" />
                      ) : (
                        <Bot className="h-4 w-4" />
                      )}
                      <span className="text-xs opacity-75">
                        {new Date(msg.timestamp).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    <div 
                      className="text-sm"
                      dangerouslySetInnerHTML={{ __html: formatMessage(msg.content) }}
                    />
                  </div>
                </div>
              ))}
              
              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-gray-100 rounded-lg p-3">
                    <Loader2 className="h-5 w-5 animate-spin text-green-600" />
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </CardContent>

            {/* Input Section */}
            <div className="border-t p-4">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && sendMessage(inputMessage)}
                  placeholder="Décrivez votre intervention..."
                  className="flex-1 p-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  disabled={isLoading || isRecording}
                />
                
                <Button
                  onClick={() => isRecording ? stopRecording() : startRecording()}
                  className={`${isRecording ? 'bg-red-600 hover:bg-red-700' : 'bg-gray-600 hover:bg-gray-700'}`}
                  disabled={isLoading}
                >
                  {isRecording ? (
                    <MicOff className="h-5 w-5" />
                  ) : (
                    <Mic className="h-5 w-5" />
                  )}
                </Button>
                
                <Button
                  onClick={() => sendMessage(inputMessage)}
                  className="bg-green-600 hover:bg-green-700"
                  disabled={isLoading || !inputMessage.trim()}
                >
                  <Send className="h-5 w-5" />
                </Button>
              </div>
              
              {isRecording && (
                <p className="text-sm text-red-600 mt-2 flex items-center gap-1">
                  <span className="animate-pulse">●</span> Enregistrement en cours... Cliquez pour arrêter
                </p>
              )}
            </div>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Intervention Created */}
          {interventionCreated && (
            <Card className="border-green-300 bg-green-50">
              <CardHeader className="py-3">
                <CardTitle className="text-lg flex items-center gap-2 text-green-700">
                  <CheckCircle className="h-5 w-5" />
                  Intervention Créée !
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <Building className="h-4 w-4 text-gray-500" />
                  <span><strong>Client:</strong> {interventionCreated.client_nom}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Wrench className="h-4 w-4 text-gray-500" />
                  <span><strong>Opération:</strong> {interventionCreated.operation}</span>
                </div>
                {interventionCreated.materiel && (
                  <div className="flex items-center gap-2">
                    <Wrench className="h-4 w-4 text-gray-500" />
                    <span><strong>Matériel:</strong> {interventionCreated.materiel}</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-gray-500" />
                  <span><strong>Durée:</strong> {interventionCreated.heures_travaillees}h</span>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-gray-500" />
                  <span><strong>Parcelles:</strong> {interventionCreated.parcelles?.map(p => p.nom).join(', ') || 'N/A'}</span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Help Card */}
          <Card>
            <CardHeader className="py-3">
              <CardTitle className="text-lg">Guide d'utilisation</CardTitle>
            </CardHeader>
            <CardContent className="text-sm space-y-3">
              <div>
                <h4 className="font-medium mb-1">💬 Par texte</h4>
                <p className="text-gray-600">Tapez votre intervention dans le champ de saisie et appuyez sur Entrée.</p>
              </div>
              
              <div>
                <h4 className="font-medium mb-1">🎤 Par la voix</h4>
                <p className="text-gray-600">Cliquez sur le micro, parlez, puis cliquez à nouveau pour arrêter.</p>
              </div>

              <div>
                <h4 className="font-medium mb-1">📝 Informations à fournir</h4>
                <ul className="text-gray-600 list-disc list-inside">
                  <li>Nom du client</li>
                  <li>Parcelle(s) concernée(s)</li>
                  <li>Type d'opération</li>
                  <li>Matériel utilisé (optionnel)</li>
                  <li>Nombre d'heures</li>
                </ul>
              </div>

              <div className="bg-yellow-50 p-3 rounded-lg">
                <h4 className="font-medium mb-1 text-yellow-800">💡 Exemple</h4>
                <p className="text-yellow-700 italic">
                  "J'ai fait 5 heures de vendange machine chez Domaine du Gourget sur les parcelles Nord et Sud avec le tracteur 150CV"
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
